/**
 * Typography and box styling for a single block, shared by the renderer and
 * the editor's controls.
 *
 * One module rather than two lists because the editor offers what the
 * renderer can draw. When those drifted apart the symptom was silent: a font
 * chosen in a dropdown that the page never applied, with nothing anywhere to
 * say why.
 */

/**
 * Font choices as stacks, not webfont links.
 *
 * A brand's public page is inline-styled and loads no stylesheet of its own,
 * so a Google font would mean a blocking request on every visit to render one
 * heading. These are faces already on the machines people browse with, each
 * with a fallback chain ending in a generic family so nothing lands on Times
 * by accident.
 */
export const FONT_STACKS = {
  default: '',
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
  slab: 'Rockwell, "Roboto Slab", "Courier Bold", Georgia, serif',
  mono: '"SF Mono", SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  rounded: '"Avenir Next", Avenir, "Segoe UI", "Trebuchet MS", sans-serif',
  condensed: '"Oswald", "Arial Narrow", "Helvetica Neue Condensed", Impact, sans-serif',
  humanist: 'Optima, "Gill Sans", "Gill Sans MT", "Segoe UI", sans-serif',
};

export const FONT_OPTIONS = [
  { value: 'default', label: 'Site default' },
  { value: 'sans', label: 'Sans — clean and modern' },
  { value: 'serif', label: 'Serif — traditional' },
  { value: 'slab', label: 'Slab — solid and bold' },
  { value: 'rounded', label: 'Rounded — friendly' },
  { value: 'condensed', label: 'Condensed — narrow' },
  { value: 'humanist', label: 'Humanist — soft edges' },
  { value: 'mono', label: 'Typewriter' },
];

export const WEIGHT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'Semi-bold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'Extra bold' },
];

export const ALIGN_OPTIONS = ['left', 'center', 'right'];

const num = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v))
  ? undefined
  : Number(v));

/**
 * The CSS a block's `styles` object describes.
 *
 * Every key is optional and an unset one produces no declaration at all,
 * rather than a default. That matters: a block with no font size has to keep
 * inheriting the size its section's layout gave it, and writing `fontSize:
 * 16` for "unset" would flatten every hero headline to body text.
 */
export const blockStyle = (component = {}) => {
  const st = component.styles || {};
  const out = {};

  if (st.textColor) out.color = st.textColor;
  if (st.backgroundColor) out.backgroundColor = st.backgroundColor;

  const stack = FONT_STACKS[st.fontFamily];
  if (stack) out.fontFamily = stack;

  const size = num(st.fontSize);
  if (size) out.fontSize = `${size}px`;

  if (st.fontWeight) out.fontWeight = st.fontWeight;
  if (st.italic) out.fontStyle = 'italic';
  if (st.underline) out.textDecoration = 'underline';
  if (st.uppercase) out.textTransform = 'uppercase';
  if (ALIGN_OPTIONS.includes(st.textAlign)) out.textAlign = st.textAlign;

  const lh = num(st.lineHeight);
  if (lh) out.lineHeight = lh;

  const ls = num(st.letterSpacing);
  if (ls !== undefined && ls !== 0) out.letterSpacing = `${ls}px`;

  const pad = num(st.padding);
  if (pad !== undefined) out.padding = `${pad}px`;

  const radius = num(st.borderRadius);
  if (radius !== undefined) out.borderRadius = `${radius}px`;

  return out;
};

/**
 * Rebuild the block tree from the flat list the API returns.
 *
 * Blocks carry a `parent_id` and the section lists all of them flat, so the
 * shape has to be recovered here. Anything whose parent is missing — hidden
 * by the public filter, or deleted — is dropped rather than promoted to the
 * top: a card's contents spilling into the page because the card was switched
 * off is a worse failure than the contents not appearing.
 */
export const buildBlockTree = (components = []) => {
  const byId = new Map();
  components.forEach((c) => byId.set(String(c._id), { ...c, children: [] }));

  const roots = [];
  components.forEach((c) => {
    const node = byId.get(String(c._id));
    const parentId = c.parent_id ? String(c.parent_id) : null;
    if (!parentId) { roots.push(node); return; }
    const parent = byId.get(parentId);
    if (parent) parent.children.push(node);
  });

  const sortDeep = (arr) => {
    arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    arr.forEach((n) => sortDeep(n.children));
  };
  sortDeep(roots);
  return roots;
};
